import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadRejections } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { XCircle, Truck, Factory, Fire, DownloadSimple } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const FILTERS = [
  { key: "all", label: "All" },
  { key: "po", label: "PO Rejections" },
  { key: "plan", label: "Plan Scrap" },
  { key: "production", label: "On-line" },
];

export default function Rejections() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rejections").then((r) => setData(r.data)).catch(() => setData({ rejections: [], count: 0 }));
  }, []);

  const rows = (data?.rejections || []).filter((r) => (filter === "all" ? true : r.type === filter));

  return (
    <>
      <PageHeader title="Rejections & Scrap" subtitle="Quality control log">
        {(data?.count ?? 0) > 0 && (
          <Button onClick={() => downloadRejections().catch(() => toast.error("Export failed"))} data-testid="export-rejections-btn" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider text-xs">
            <DownloadSimple size={16} weight="bold" className="mr-1" /> Export Excel
          </Button>
        )}
      </PageHeader>
      <div className="p-4 sm:p-8 space-y-4">
        {data && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-zinc-300 p-5" data-testid="rej-stat-total">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Events</p>
              <p className="font-mono text-3xl font-bold text-zinc-900 mt-2">{data.count}</p>
            </div>
            <div className="bg-white border border-zinc-300 p-5" data-testid="rej-stat-po">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">PO Rejected</p>
              <p className="font-mono text-3xl font-bold text-[#DC2626] mt-2">{data.po_qty ?? 0}</p>
              <p className="text-[11px] text-zinc-400 mt-1">returned to pending</p>
            </div>
            <div className="bg-white border border-zinc-300 p-5" data-testid="rej-stat-plan">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Plan Scrap</p>
              <p className="font-mono text-3xl font-bold text-[#DC2626] mt-2">{data.plan_qty ?? 0}</p>
              <p className="text-[11px] text-zinc-400 mt-1">permanent loss</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="rej-filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`rej-filter-${f.key}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors duration-100 ${filter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-zinc-300" data-testid="rej-list">
          {rows.length === 0 ? (
            <p className="p-12 text-center text-sm text-zinc-400">No rejections or scrap recorded.</p>
          ) : (
            <div className="divide-y divide-zinc-200">
              {rows.map((r, i) => (
                <button
                  key={`${r.type}-${r.ref_id}-${i}`}
                  onClick={() => navigate(r.type === "po" ? `/purchase-orders/${r.ref_id}/timeline` : r.type === "production" ? "/production" : "/production-plans")}
                  data-testid={`rej-row-${i}`}
                  className="w-full text-left flex flex-wrap items-center justify-between gap-3 px-6 py-3 hover:bg-zinc-50 transition-colors duration-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {r.type === "po" ? <Truck size={16} weight="bold" className="text-primary shrink-0" /> : r.type === "production" ? <Fire size={16} weight="bold" className="text-[#EA580C] shrink-0" /> : <Factory size={16} weight="bold" className="text-[#0891B2] shrink-0" />}
                    <span className="font-mono text-xs font-bold text-primary">{r.ref_number}</span>
                    <span className="text-sm text-zinc-900 truncate">{r.material_name}</span>
                    {r.party && <span className="text-[11px] text-zinc-400 truncate">· {r.party}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    {r.reason && <span className="text-[11px] text-zinc-500 italic max-w-[220px] truncate">{r.reason}</span>}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${r.type === "po" ? "bg-[#EA580C]" : r.type === "production" ? "bg-[#B91C1C]" : "bg-[#DC2626]"}`}>
                      <XCircle size={11} weight="bold" /> {r.type === "po" ? "Rejected" : r.type === "production" ? "On-line" : "Scrap"}
                    </span>
                    <span className="font-mono text-sm font-bold text-[#DC2626]">{r.qty} {r.unit}</span>
                    <span className="text-[11px] text-zinc-400 hidden sm:inline">{fmt(r.at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
